import classic from 'ember-classic-decorator';
/* global analytics */
import { getTrackingStatus } from 'client-portal/utils/sample-track';
import { task } from 'ember-concurrency';
import { testAwareTimeout, waitForRender } from 'ember-simplepractice/utils/waiters';
import Service, { service } from '@ember/service';
import config from 'client-portal/config/environment';

const SOURCE_APPLICATION = 'Client Portal';
const INITIATOR = 'User';

@classic
export default class AnalyticsService extends Service {
  @service currentPractice;
  @service consentManager;
  @service cookies;
  @service router;
  @service session;

  clientIdentified = false;
  clientProperties = {};
  isSampling = false;
  isWidget = true;
  trackingEnabled = true;

  init() {
    super.init(...arguments);

    if (config.analyticsEnvironment === 'PROD') {
      this.isSampling = true;
    }

    this.isWidget = this.router.location.path?.startsWith('/widget');

    this.trackingEnabled =
      !!this.session.anonymousId || getTrackingStatus(this.cookies, this.isWidget, this.isSampling);

    this.router.on('routeDidChange', () => {
      this.pageViewTask.perform();
    });
  }

  @(task(function* () {
    yield waitForRender();
    const currentPath = this.router.currentURL?.split('?')[0];
    if (this._lastPageViewedPath === currentPath) return;
    if (config.environment !== 'test') {
      yield testAwareTimeout(50);
    }
    this._lastPageViewedPath = currentPath;
    this.page();
  }).drop())
  pageViewTask;

  identify(currentClient) {
    if (
      typeof analytics === 'undefined' ||
      this.clientIdentified ||
      !currentClient ||
      !this.trackingEnabled
    ) {
      return;
    }

    this.set('clientIdentified', true);
    this.set('clientProperties', {
      'client_id': currentClient.hashedId,
      'practice_id': this.get('currentPractice.id'),
    });

    let identifyProperties = {
      'practice_id': this.clientProperties.practice_id,
      'source_application': SOURCE_APPLICATION,
    };

    analytics.identify(currentClient.hashedId, identifyProperties, this.consentOptions);
  }

  track(eventName, properties = {}) {
    if (!this.clientIdentified || !this.trackingEnabled) {
      return;
    }

    let eventProperties = {
      'initiator': INITIATOR,
      'source_application': SOURCE_APPLICATION,
      ...this.clientProperties,
      ...properties,
    };

    analytics.track(eventName, eventProperties, this.consentOptions);
  }

  trackAnonymously(eventName, properties = {}) {
    if (typeof analytics === 'undefined' || !this.trackingEnabled) {
      return;
    }

    let eventProperties = {
      'practice_id': this.get('currentPractice.id'),
      'initiator': INITIATOR,
      'source_application': SOURCE_APPLICATION,
      ...properties,
    };

    analytics.track(eventName, eventProperties, this.consentOptions);
  }

  selectiveTrack(eventName, properties = {}) {
    this.clientIdentified
      ? this.track(eventName, properties)
      : this.trackAnonymously(eventName, properties);
  }

  clearAttributes() {
    this.set('clientIdentified', false);
    this.set('clientProperties', {});
  }

  page() {
    if (typeof analytics === 'undefined' || !this.trackingEnabled) {
      return;
    }

    analytics.page({}, this.consentOptions);
  }

  setAnonymousId(anonymousId) {
    if (typeof analytics === 'undefined' || !this.trackingEnabled) {
      return;
    }
    analytics.setAnonymousId(anonymousId);
  }

  get consentOptions() {
    return this.currentPractice.get('featureDatagrailSegmentConsentClients')
      ? this.consentManager.getConsentContext()
      : {};
  }

  get anonymousId() {
    if (
      typeof analytics === 'undefined' ||
      typeof analytics.user !== 'function' ||
      !this.trackingEnabled
    ) {
      return null;
    }
    return analytics.user().anonymousId();
  }
}
